import {
  taskOrderEnums,
  WORK_ORDER_STEPS,
  workOrderHistoryEnums,
} from '@/types/enums';
import z from 'zod';

const getFrames = {
  body: z.never(),
  response: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      quantity: z.number(),
    }),
  ),
  params: z.object({
    project_id: z.coerce.number(),
    release_id: z.coerce.number(),
  }),
  query: z.never(),
};
const getFramesSchema = z.object(getFrames);
export type GetFrames = z.infer<typeof getFramesSchema>;

const getFrame = {
  body: z.never(),
  response: z.object({
    id: z.number(),
    name: z.string(),
    quantity: z.number(),
    drawing: z.string().nullable(),
    parts: z.array(
      z.object({
        part_number: z.string(),
        attributes: z.object({}),
        quantity: z.number(),
      }),
    ),
  }),

  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const getFrameSchema = z.object(getFrame);
export type GetFrame = z.infer<typeof getFrameSchema>;

// GET -> /api/production/frames/{frame_id}/detail/
const getFrameDetails = {
  body: z.never(),
  response: z.object({
    code: z.string(),
    message: z.string(),
    data: z.object({
      id: z.number(),
      name: z.string(),
      quantity: z.number(),
      project_id: z.number(),
      release_id: z.number(),
      frame_masterfile_id: z.number(),
      frame_masterfile_revision_id: z.number(),
      frame_drawing_url: z.url(),
      parts: z
        .object({
          part_id: z.number(),
          part_number: z.string(),
          attributes: z.object({}),
          required_per_frame: z.number(),
          required_total: z.number(),
          available_qty: z.number(),
          short_qty: z.number(),
        })
        .array(),
      work_orders: z
        .object({
          id: z.number(),
          work_order_identifier: z.string(),
          status: z.enum(taskOrderEnums?.map((status) => status?.value)),
          description: z.string(),
          total_quantity: z.number(),
          assembled_quantity: z.number(),
          glazed_quantity: z.number(),
          packed_quantity: z.number(),
          shipped_quantity: z.number(),
          installed_quantity: z.number(),
          assigned_users: z
            .object({
              id: z.number(),
              display_name: z.string(),
            })
            .array(),
          created_at: z.date(), // '2026-03-04T16:06:46.012439Z'
          updated_at: z.date(), // '2026-03-09T10:55:03.984463Z',
        })
        .array(),
      history: z
        .object({
          id: z.number(),
          step: z.enum(workOrderHistoryEnums?.map((status) => status?.value)),
          quantity: z.number(),
          checklist_snapshot: z.unknown().nullable(),
          logged_by: z.number().nullable(),
          logged_by_display: z.string().nullable(),
          is_override: z.boolean().optional(),
          override_reason: z.string().nullable().optional(),
          assignment_snapshot: z
            .object({
              assigned_user_ids: z.array(z.number()).optional(),
              assigned_user_names: z.array(z.string()).optional(),
              completed_by_id: z.number().nullable().optional(),
              completed_by_name: z.string().nullable().optional(),
              completed_by_was_assigned: z.boolean().optional(),
              is_override: z.boolean().optional(),
              override_reason: z.string().nullable().optional(),
            })
            .nullable()
            .optional(),
          created_at: z.date(), // '2026-03-09T10:55:03.993820Z'
        })
        .array(),
      created_at: z.date(), // '2026-03-02T19:56:53.497024Z'
      updated_at: z.date(), // '2026-03-02T19:57:00.780276Z'
    }),
  }),
  params: z.object({
    frame_id: z.coerce.number(),
  }),
  query: z.never(),
};
const getFrameDetailsSchema = z.object(getFrameDetails);
export type GetFrameDetails = z.infer<typeof getFrameDetailsSchema>;

const getFramesByStep = {
  body: z.never(),
  response: z.array(
    z.object({
      frame_id: z.number(),
      frame_name: z.string(),
      work_order_id: z.number(),
      work_order_identifier: z.string(),
      status: z.enum(taskOrderEnums?.map((status) => status?.value)),
      total_quantity: z.number().optional(),
      previous_completed_quantity: z.number().nullable().optional(),
      current_completed_quantity: z.number().optional(),
      remaining_quantity: z.number(),
      ready: z.boolean().optional(),
      blocked_reason: z.string().nullable().optional(),
      assigned_users: z
        .object({
          id: z.number(),
          display_name: z.string(),
        })
        .array()
        .optional(),
    }),
  ),
  params: z.object({
    release_id: z.coerce.number(),
    step: z.enum(WORK_ORDER_STEPS),
  }),
  query: z
    .object({
      scope: z.enum(['assigned', 'all', 'unassigned']).optional(),
      assigned_user: z.coerce.number().optional(),
      ready: z.boolean().optional(),
      status: z.string().optional(),
      frame: z.string().optional(),
      work_order: z.string().optional(),
    })
    .optional(),
};
const getFramesByStepSchema = z.object(getFramesByStep);
export type GetFramesByStep = z.infer<typeof getFramesByStepSchema>;

const postCompleteFrameStep = {
  body: z.object({
    step: z.enum(WORK_ORDER_STEPS),
    quantity: z.coerce.number(),
    checklist_answers: z
      .object({
        item: z.string().optional(),
        checked: z.boolean().optional(),
        status: z.enum(['PASS', 'FAIL', 'NA']).optional(),
        notes: z.string().optional(),
      })
      .array()
      .optional(),
    qc_event_id: z.coerce.number().nullable().optional(),
    qc_result: z.enum(['PASS', 'FAIL', 'INFO']).optional(),
    completion_context: z.record(z.string(), z.unknown()).optional(),
  }),
  response: z.object({
    detail: z.string(),
    work_order_id: z.number().optional(),
    frame_id: z.number().optional(),
    remaining_quantity: z.number().optional(),
    status: z.string().optional(),
    warning: z.string().optional(),
    inventory_shortages: z.array(z.record(z.string(), z.unknown())).optional(),
    inventory_consumed: z.array(z.record(z.string(), z.unknown())).optional(),
  }),
  params: z.object({
    frame_id: z.coerce.number(),
  }),
  query: z.never(),
};
const postCompleteFrameStepSchema = z.object(postCompleteFrameStep);
export type PostCompleteFrameStep = z.infer<typeof postCompleteFrameStepSchema>;

// GET '/api/production/releases/{release_id}/frames/'
const getFramesByRelease = {
  body: z.never(),
  response: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      quantity: z.number(),
      work_order_id: z.number(),
      work_order_identifier: z.string(),
      status: z.string(),
      assembled_quantity: z.number(),
      glazed_quantity: z.number(),
      packed_quantity: z.number(),
      shipped_quantity: z.number(),
      installed_quantity: z.number(),
    }),
  ),
  params: z.object({
    release_id: z.coerce.number(),
  }),
  query: z.never(),
};
const getFramesByReleaseSchema = z.object(getFramesByRelease);
export type GetFramesByRelease = z.infer<typeof getFramesByReleaseSchema>;
